import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

/**
 * Listens for a newly deployed app version (Angular service worker) and offers a
 * one-tap reload. Without this, an installed PWA keeps running the cached build
 * until every tab is closed — dispatchers keep boards open for whole shifts, so
 * they would stay on stale code indefinitely.
 *
 * No-op in dev builds (SwUpdate.isEnabled is false there).
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  constructor(
    private updates: SwUpdate,
    private snack: MatSnackBar,
    private translate: TranslateService,
  ) {}

  init(): void {
    if (!this.updates.isEnabled) return;

    this.updates.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => {
        const ref = this.snack.open(
          this.translate.instant('pwa.update_ready'),
          this.translate.instant('pwa.update_action'),
          { horizontalPosition: 'right', verticalPosition: 'top' }, // sticky until acted on
        );
        ref.onAction().subscribe(() => {
          this.updates.activateUpdate().then(() => document.location.reload());
        });
      });

    // A corrupted cache (e.g. a partial deploy) makes ngsw bail out — recover by
    // reloading, which re-fetches everything from the network.
    this.updates.unrecoverable.subscribe(() => document.location.reload());
  }
}
